import React, { useState, useEffect } from 'react';
import { Paper, Typography, Table, TableBody, TableCell, TableRow, Divider, Box, TextField, Avatar, Button, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useApiUtil } from "@/utils/apiUtil";
import DOMPurify from 'dompurify';

const BoardDetailPopup = ({ board, boardType, setUploadedFiles, boardInfo }) => {
  //console.log("BoardDetailPopup", board, boardType, setUploadedFiles, boardInfo)
  const [refreshFlag, setRefreshFlag] = useState(false); // 리스트 새로고침용
  const [comments, setComments] = useState([]);
  const [commentInput, setCommentInput] = useState('');
  const [editSeq, setEditSeq] = useState(null);
  const [editInput, setEditInput] = useState('');
  const [boardFile, setBoardFile] = useState([]);
  const userId = useSelector((state) => state.user.userId);
  const { gfnCallApi } = useApiUtil();

  const tableSx = {
    mb: 2,
    border: '1px solid #ddd',
    borderCollapse: 'collapse',
    '& td': {
      border: '1px solid #ddd',
      fontSize: 13,
      py: 1,
    },
    '& th': {
      border: '1px solid #ddd',
    },
  };  
  const setCount = async () => {
    const payload = {
      blbdCd: board.BLBD_CD,
      notiNo: board.NOTI_NO
    };

    const result = await gfnCallApi({
      method: "post",
      url: "inqCount",
      data: payload,
      showMsg: false, // "저장됨" 토스트 자동 표시
    });

    setRefreshFlag((prev) => !prev); // 다시 불러오기
};

  const searchComments = async () => {
    const result = await gfnCallApi({
      method: "post",
      url: "replyList",
      data: { BOARD_CD: board.BLBD_CD, NOTI_NO: board.NOTI_NO },
      showMsg: false,
    });
    if(!result || result.code < 0) return;
    const data = result.data;
    setComments(data);
  }

  // 파일 서치
  const searchFiles = async () => {
    const result = await gfnCallApi({
      method: "post",
      url: "boardFileList",
      data: { fileId: board.FILE_ID },
      showMsg: false,
    });
    if(!result || result.code < 0) return;
    const data = result.data;

    setBoardFile(data);
    setUploadedFiles(data);
  };

  // 댓글 등록 핸들러
  const handleComment = (query, replySeq, content) => {
    switch(query) {
      case 'insert':
        handleSave('insert', replySeq, content);
      break;
      case 'update':
        handleSave('update', replySeq, content);
        setEditSeq(null);
        setEditInput('');
      break;
      case 'delete':
        if (!window.confirm('삭제하시겠습니까?')) return;
        handleSave('delete', replySeq, content);
      break;
      default:
      break;
    }
  };

  const handleSave = async (query, replySeq, content) => {
    const payload = {
      BLBD_CD: board.BLBD_CD,
      NOTI_NO: board.NOTI_NO,
      userId: userId,
      REPLY_CNTN: content,
      replyQuery: query,
      REPLY_SEQ: replySeq
    };

    const result = await gfnCallApi({
      method: "post",
      url: "replySave",
      data: payload,
      showMsg: false, // "저장됨" 토스트 자동 표시
    });
    if(!result || result.code < 0) return;
    const data = result.data;
    setCommentInput(''); // 변경 리스트 초기화
    setRefreshFlag((prev) => !prev); // 다시 불러오기
};

  // 댓글 수정 모드 진입
  const handleEditComment = (id, content) => {
    setEditSeq(id);
    setEditInput(content);
  };

  // 댓글 수정 취소
  const handleCancelEdit = () => {
    setEditSeq(null);
    setEditInput('');
  };

  useEffect(() => {
    searchComments();
  }, [refreshFlag]);

  useEffect(() => {
    setCount();
    if(!board.INQ_CNT) board.INQ_CNT = 0;
    board.INQ_CNT += 1;
    searchFiles();
  }, []);

  return (
    <Paper
      sx={{
        width: 820,
        height: 540,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* 상단: 게시글 정보 }
      <Typography variant="h6" sx={{ mb: 1, flexShrink: 0 }}>
        <strong>{boardType}</strong>
      </Typography>
      {*/}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          pr: 1,
          minHeight: 0,
        }}
      >
        {/* 게시글 정보 */}
        <Table size="small" sx={tableSx}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ bgcolor: '#f5f6fa', fontWeight: 600, width: 80 }}>
                제목
              </TableCell>
              <TableCell colSpan={3}>{board?.TITLE}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={{ bgcolor: '#f5f6fa', fontWeight: 600 }}>
                작성자
              </TableCell>
              <TableCell>{board?.INPT_ID}</TableCell>
              <TableCell sx={{ bgcolor: '#f5f6fa', fontWeight: 600 }}>
                조회수
              </TableCell>
              <TableCell>{board?.INQ_CNT}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={{ bgcolor: '#f5f6fa', fontWeight: 600 }}>
                작성일
              </TableCell>
              <TableCell>
                {dayjs(board?.INPT_DTTM).format('YYYY-MM-DD HH:mm')}
              </TableCell>
              <TableCell sx={{ bgcolor: '#f5f6fa', fontWeight: 600 }}>
                댓글수
              </TableCell>
              <TableCell>{board?.replyCnt}</TableCell>
            </TableRow>
          </TableBody>
        </Table>


          {/* 본문 내용 */}
          <Table size="small" sx={tableSx}>
            <TableBody>
              <TableRow>
                <TableCell
                  sx={{
                    bgcolor: '#f5f6fa',
                    width: 80,
                    fontWeight: 600,
                    verticalAlign: 'top',
                  }}
                >
                  내용
                </TableCell>
                <TableCell colSpan={3} sx={{ p: 0 }}>
                  <Box
                    sx={{
                      p: 2,
                      minHeight: 200,
                      lineHeight: 1.6,
                      fontSize: 14,
                    }}
                    dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(board?.CNTN || '')}}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>


      {/* 첨부파일 영역 */}
      {boardInfo?.APND_YN === '1' ? (
          <Table size="small" sx={tableSx}>
            <TableBody>
              <TableRow>
                <TableCell sx={{ width: 80, fontWeight: 600, bgcolor: '#f5f6fa' }}>
                  첨부파일
                </TableCell>
                <TableCell colSpan={3}>
                  <Box
                    sx={{
                      maxHeight: 120,
                      overflowY: 'auto',
                    }}
                  >
                    {boardFile.map((file, idx) => (
                      <Box key={idx} sx={{ mb: 0.5 }}>
                        <a
                          href={`/api/front/file/download?fileId=${file.FILE_ID}&fileSeq=${file.FILE_SEQ}`}
                          style={{ color: '#1976d2', textDecoration: 'underline' }}
                        >
                          {file.OTXT_FILE_NM}
                        </a>
                      </Box>
                    ))}
                  </Box>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

      ) : null }

      {/* 댓글 영역 */}
      {boardInfo?.REPLY_YN === '1' ? (
        <>
          <Box
            sx={{
              mt: 2,
              border: '1px solid #ddd',
              borderRadius: 1,
              bgcolor: '#fafafa',
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              댓글
            </Typography>

            <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
              {/* 댓글 리스트 */}
              {comments.map((comment) => (
                <Box key={comment.REPLY_SEQ} sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
                  <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: '#bdbdbd', fontSize: 16 }}>
                    {comment.INPT_ID.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{comment.INPT_ID}</Typography>
                    {editSeq === comment.REPLY_SEQ ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          size="small"
                          value={editInput}
                          onChange={e => setEditInput(e.target.value)}
                          sx={{ flex: 1, bgcolor: '#fff' }}
                        />
                        <IconButton onClick={() => handleComment('update', comment.REPLY_SEQ, editInput)} color="primary"><SaveIcon /></IconButton>
                        <IconButton onClick={handleCancelEdit}><CancelIcon /></IconButton>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: "#555", mb: 0.5 }}>{comment.REPLY_CNTN}</Typography>
                    )}
                    <Typography variant="caption" sx={{ color: "#999" }}>{comment.INPT_DTTM}</Typography>
                  </Box>
                  {/* 우측 버튼 영역 */}
                  {editSeq !== comment.REPLY_SEQ && comment.INPT_ID === userId && (
                    <Box sx={{ ml: 1 }}>
                      <IconButton onClick={() => handleEditComment(comment.REPLY_SEQ, comment.REPLY_CNTN)} size="small"><EditIcon fontSize="small" /></IconButton>
                      <IconButton onClick={() => handleComment('delete', comment.REPLY_SEQ)} size="small"><DeleteIcon fontSize="small" /></IconButton>
                    </Box>
                  )}
                </Box>
              ))}
              {/* 댓글 입력창 */}
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: '#bdbdbd', fontSize: 16 }}>
                  {userId.charAt(0).toUpperCase()}
                </Avatar>
                <TextField
                  size="small"
                  placeholder="댓글을 입력하세요"
                  value={commentInput}
                  onChange={e => setCommentInput(e.target.value)}
                  sx={{ flex: 1, mr: 1, bgcolor: '#fff' }}
                  inputProps={{ style: { fontSize: 14, padding: '8px 10px' } }}
                />
                <Button
                  variant="contained"
                  sx={{ minWidth: 100, height: 40, fontWeight: 600 }}
                  onClick={() => handleComment('insert', '', commentInput)}
                >
                  댓글 등록
                </Button>
              </Box>
            </Box>  
          </Box>
        </>
      ) : null }
      </Box>
    </Paper>
  );
};

export default BoardDetailPopup;